import { Component, ViewContainerRef } from "@angular/core";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { REPLACE_KEY, StorageKeys } from "../../constants/app-constants";
import { LocalStorageService } from "../../services/local-storage.service";
import { User } from "../../models/user.interface";
import { MultiSelectComponent } from "../select/multi-select.component";
import { NzDropDownDirective, NzDropdownMenuComponent } from "ng-zorro-antd/dropdown";
import { NzMenuDirective, NzMenuItemComponent } from "ng-zorro-antd/menu";
import { AuthService } from "../../services/auth.service";
import { ActivityLogSearchBarConfig } from "./config/search-bar.config";
import { FormBuilder, FormGroup, FormsModule } from "@angular/forms";
import { DynamicFormService } from "../../services/dynamic-form.service";
import { DynamicFormComponent } from "../dynamic-form/dynamic-form.component";
import { Router, RouterLink } from "@angular/router";
import { HeaderService } from "../../services/header.service";
import { ActivityLogService } from "../../services/activity-log.service";
import { ModalComponent } from "../modal/modal.component";
import { InventoryService } from "../../services/inventory-service";
import { LoadingService } from "../../services/loading.service";
import { Subject } from "rxjs";
import {
  ActivityLogQueryParams,
  ActivityLogSearchBar,
  ModalConfig,
  NewTabConfig,
  SearchBarConfigInterface
} from "../../models/activity-log-search.intercafe";
import { NgStyle } from "@angular/common";

export const HEADER_OPTION_VALUES = {
  CONTAINER: "CONTAINER",
  SHIPPING_BILL_NUMBER: "SHIPPING_BILL_NUMBER",
  BL_NUMBER: "BL_NUMBER",
  IGM_ITEM_NUMBER: "IGM_ITEM_NUMBER",
  BOE_NUMBER: "BOE_NUMBER",
  EX_BOE_NUMBER: "EX_BOE_NUMBER",
  VIA_NUMBER: "VIA_NUMBER"
};

export type SearchBarKey = keyof typeof HEADER_OPTION_VALUES;

@Component({
  selector: "app-header",
  standalone: true,
  imports: [
    SvgIconComponent,
    MultiSelectComponent,
    NzDropDownDirective,
    NzDropdownMenuComponent,
    NzMenuDirective,
    NzMenuItemComponent,
    DynamicFormComponent,
    FormsModule,
    RouterLink,
    NgStyle
  ],
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss"
})
export class HeaderComponent {
  userProfile!: User;
  headerOptions = [
    {
      label: "Container",
      value: HEADER_OPTION_VALUES.CONTAINER
    },
    {
      label: "BL No.",
      value: HEADER_OPTION_VALUES.BL_NUMBER
    },
    {
      label: "VIA No.",
      value: HEADER_OPTION_VALUES.VIA_NUMBER
    },
    {
      label: "Shipping Bill",
      value: HEADER_OPTION_VALUES.SHIPPING_BILL_NUMBER
    },
    {
      label: "IGM Item",
      value: HEADER_OPTION_VALUES.IGM_ITEM_NUMBER
    },
    {
      label: "BOE No",
      value: HEADER_OPTION_VALUES.BOE_NUMBER
    },
    {
      label: "Ex BOE No",
      value: HEADER_OPTION_VALUES.EX_BOE_NUMBER
    }
  ];
  activityLogSearchBarConfig: ActivityLogSearchBar = ActivityLogSearchBarConfig;
  selectedSearchBarConfig!: SearchBarConfigInterface;
  searchBarForm!: FormGroup;

  private destroy$ = new Subject<void>();

  constructor(private localStorageService: LocalStorageService,
              private authService: AuthService,
              private dynamicFormService: DynamicFormService,
              private fb: FormBuilder,
              private router: Router,
              private headerService: HeaderService,
              private activityLogService: ActivityLogService,
              private inventoryService: InventoryService,
              private viewContainerRef: ViewContainerRef,
              private loadingService: LoadingService) {
    this.searchBarForm = this.fb.group({});
  }

  ngOnInit() {
    this.getUserProfile();
    this.handleOptionsChange(this.headerOptions[0]); //set the default option
    this.activityLogService.getContainerLogTrigger().subscribe((data) =>
      this.openActivityLogModal(data.containerId, this.activityLogSearchBarConfig[HEADER_OPTION_VALUES.CONTAINER as SearchBarKey].modalConfig));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getUserProfile() {
    const userProfile = this.localStorageService.getItem(StorageKeys.USER_PROFILE);
    if (userProfile) {
      this.userProfile = JSON.parse(userProfile.toString());
    } else {
      setTimeout(() => {
        const userProfile = this.localStorageService.getItem(StorageKeys.USER_PROFILE);
        if (userProfile) {
          this.userProfile = JSON.parse(userProfile.toString());
        }
      }, 1000);
    }
  }

  onLogout() {
    this.authService.logout();
  }

  handleOptionsChange(event: { label: string; value: string }) {
    this.searchBarForm.reset();
    const selectedHeaderOption = event.value;
    console.log(event);

    this.selectedSearchBarConfig = this.activityLogSearchBarConfig[selectedHeaderOption as SearchBarKey];
    if (!this.selectedSearchBarConfig) {
      console.warn("No configuration found for selected header option", selectedHeaderOption);
      return;
    }
    this.dynamicFormService.addControlsToForm(this.searchBarForm, this.selectedSearchBarConfig.dynamicFormConfig);
  }

  onSearchEnter() {
    const dataType = this.selectedSearchBarConfig?.dynamicFormConfig?.formRows?.[0]
      ?.formGroups?.[0]?.control?.fieldInfo?.dataType;
    if (dataType !== "text") return;
    this.handleSubmit();
  }

  handleSubmit() {
    this.searchBarForm.markAllAsTouched();
    if (this.searchBarForm.invalid) return;

    const searchBarValue = this.searchBarForm.get("searchBar")?.value;

    switch (this.selectedSearchBarConfig.viewMode) {
      case "modal":
        this.openActivityLogModal(searchBarValue, this.selectedSearchBarConfig.modalConfig);
        break;
      case "new-tab":
        this.openUrlInNewPage(searchBarValue, this.selectedSearchBarConfig.newTabConfig);
    }
  }

  private openActivityLogModal(searchBarValue: string, modalConfig?: ModalConfig) {
    if (!modalConfig) {
      console.warn("No modal configuration provided");
      return;
    }

    const { title, component, dataKey } = modalConfig;
    const payload = { [dataKey]: searchBarValue };
    const modalComponentComponentRef = this.viewContainerRef.createComponent(ModalComponent);
    modalComponentComponentRef.instance.title = title ?? "Activity Log";
    // modalComponentComponentRef.instance.width = "60vw";
    // modalComponentComponentRef.instance.height = "80vh";
    modalComponentComponentRef.instance.close.subscribe(() => modalComponentComponentRef.destroy());

    modalComponentComponentRef.instance.loadComponent(component, payload);
  }

  private openUrlInNewPage(searchBarValue: string, newTabConfig?: NewTabConfig) {
    if (!newTabConfig) {
      console.warn("No new tab configuration provided");
      return;
    }

    const urlWithValue = newTabConfig.url.replace(REPLACE_KEY, searchBarValue);
    if (newTabConfig.queryParams) {
      const queryParamsWithValue: ActivityLogQueryParams = {
        ...newTabConfig.queryParams,
        value: newTabConfig.queryParams.value.replace(REPLACE_KEY, searchBarValue),
      };
      this.headerService.openUrlInNewTab(urlWithValue, queryParamsWithValue);
    } else {
      this.headerService.openUrlInNewTab(urlWithValue);
    }
  }

  isSelected(s: string) {
    return this.router.url === s
  }
}
